import React, { useEffect, useRef, useState } from "react";
import {
  Container,
  Card,
  Row,
  Col,
  Form,
  Button,
  Tabs,
  Tab,
  Spinner,
  Badge,
} from "react-bootstrap";
import { ToastContainer, toast } from "react-toastify";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";

const AddChartOfAccounts = () => {
  const baseUrl = process.env.REACT_APP_API_BASE_URL;
  const navigate = useNavigate();

  const userData = sessionStorage.getItem("user");
  const userObj = userData ? JSON.parse(userData) : {};

  // ✅ Edit navigation data
  const location = useLocation();
  const editNode = location?.state?.editNode || null;
  const editPath = location?.state?.path || null;

  // Tabs
  const [activeTab, setActiveTab] = useState("level2");

  // Dropdown data
  const [level1Options, setLevel1Options] = useState([]);
  const [level2Options, setLevel2Options] = useState([]);

  const [loadingLevel1, setLoadingLevel1] = useState(false);
  const [loadingLevel2, setLoadingLevel2] = useState(false);

  const [savingLevel2, setSavingLevel2] = useState(false);
  const [savingLevel3, setSavingLevel3] = useState(false);

  // Selected
  const [selectedLevel1, setSelectedLevel1] = useState("");
  const [selectedLevel2, setSelectedLevel2] = useState("");

  // ✅ Edit mode ids
  const [editLevel2Id, setEditLevel2Id] = useState(null);
  const [editLevel3Id, setEditLevel3Id] = useState(null);

  // ✅ skip reset once (for edit prefill)
  const skipLevel1ResetOnceRef = useRef(false);

  // Level2 form
  const [level2Form, setLevel2Form] = useState({
    node_name: "",
    node_code: "",
    is_active: true,
  });

  // Level3 bulk rows
  const [level3Rows, setLevel3Rows] = useState([
    { node_name: "", node_code: "", is_active: true },
  ]);

  const normalizeNode = (d) => ({
    node_id: d.node_id ?? d.id ?? Object.values(d || {})[0],
    node_name: d.node_name ?? d.name ?? "",
    node_code: d.node_code ?? d.code ?? "",
    parent_id: d.parent_id ?? null,
    node_level: d.node_level ?? null,
    is_active: d.is_active ?? true,
  });

  const selectedLevel1Obj = level1Options.find(
    (x) => String(x.node_id) === String(selectedLevel1)
  );
  const selectedLevel2Obj = level2Options.find(
    (x) => String(x.node_id) === String(selectedLevel2)
  );

  // Fetch Parent Groups (node_level=1)
  const fetchLevel1 = async () => {
    setLoadingLevel1(true);
    try {
      const res = await axios.get(`${baseUrl}/coa/levels`, {
        params: { _school_id: userObj.school_id, _node_level: 1 },
      });
      const data = Array.isArray(res.data) ? res.data : [];
      setLevel1Options(data.map(normalizeNode));
    } catch (e) {
      console.error(e);
      setLevel1Options([]);
      toast.error("Failed to load Parent Groups");
    } finally {
      setLoadingLevel1(false);
    }
  };

  // Fetch Groups by selected Parent Group (node_level=2)
  const fetchLevel2 = async (level1Id) => {
    if (!level1Id) {
      setLevel2Options([]);
      return [];
    }
    setLoadingLevel2(true);
    try {
      const res = await axios.get(`${baseUrl}/coa/levels`, {
        params: {
          _school_id: userObj.school_id,
          _node_level: 2,
          _parent_id: level1Id,
        },
      });
      const data = Array.isArray(res.data) ? res.data : [];
      const mapped = data.map(normalizeNode);
      setLevel2Options(mapped);
      return mapped;
    } catch (e) {
      console.error(e);
      setLevel2Options([]);
      toast.error("Failed to load Groups");
      return [];
    } finally {
      setLoadingLevel2(false);
    }
  };

  useEffect(() => {
    fetchLevel1();
    // eslint-disable-next-line
  }, []);

  // ✅ When Parent Group changes (manual selection)
  useEffect(() => {
    // ✅ skip reset ONCE during edit prefill
    if (skipLevel1ResetOnceRef.current) {
      skipLevel1ResetOnceRef.current = false;
      if (selectedLevel1) fetchLevel2(selectedLevel1);
      else setLevel2Options([]);
      return;
    }

    // ✅ normal manual change reset
    setSelectedLevel2("");
    setLevel2Form({ node_name: "", node_code: "", is_active: true });
    setLevel3Rows([{ node_name: "", node_code: "", is_active: true }]);
    setEditLevel2Id(null);
    setEditLevel3Id(null);

    if (selectedLevel1) fetchLevel2(selectedLevel1);
    else setLevel2Options([]);
    // eslint-disable-next-line
  }, [selectedLevel1]);

  // ✅ Prefill when coming from Tree Edit (HOLDS ALL LEVEL VALUES)
  useEffect(() => {
    const initEdit = async () => {
      if (!editNode || !editPath?.length) return;

      const l1 = editPath[0] || null;
      const l2 = editPath[1] || null;

      if (!l1?.node_id) return;

      // ✅ prevent level1 change effect from clearing level2
      skipLevel1ResetOnceRef.current = true;

      // ✅ set selected parent group
      setSelectedLevel1(String(l1.node_id));

      // ✅ fetch groups under this parent
      const groups = await fetchLevel2(String(l1.node_id));

      // ✅ decide which group to select
      let level2ToSelect = null;

      if (l2?.node_id) level2ToSelect = String(l2.node_id);
      else if (Number(editNode.node_level) === 2) level2ToSelect = String(editNode.node_id);
      else if (Number(editNode.node_level) === 3) {
        const found = groups.find(
          (g) => String(g.node_id) === String(editNode.parent_id)
        );
        if (found) level2ToSelect = String(found.node_id);
      }

      if (level2ToSelect) setSelectedLevel2(level2ToSelect);

      // ✅ set tab + forms
      if (Number(editNode.node_level) === 1) {
        setActiveTab("level2");
        setEditLevel2Id(null);
        setEditLevel3Id(null);
        setLevel2Form({ node_name: "", node_code: "", is_active: true });
        setLevel3Rows([{ node_name: "", node_code: "", is_active: true }]);
      } else if (Number(editNode.node_level) === 2) {
        setActiveTab("level2");
        setEditLevel2Id(editNode.node_id);
        setEditLevel3Id(null);
        setLevel2Form({
          node_name: editNode.node_name || "",
          node_code: editNode.node_code || "",
          is_active: !!editNode.is_active,
        });
        setLevel3Rows([{ node_name: "", node_code: "", is_active: true }]);
      } else if (Number(editNode.node_level) === 3) {
        setActiveTab("level3");
        setEditLevel3Id(editNode.node_id);
        setEditLevel2Id(null);
        setLevel2Form({ node_name: "", node_code: "", is_active: true });

        // ✅ first row is UPDATE row (has node_id)
        setLevel3Rows([
          {
            node_id: editNode.node_id,
            node_name: editNode.node_name || "",
            node_code: editNode.node_code || "",
            is_active: editNode.is_active !== false,
          },
        ]);
      }
    };

    initEdit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editNode]);

  // Save Group (node_level=2) CREATE / UPDATE
  const handleSaveLevel2 = async () => {
    if (!selectedLevel1) return toast.error("Please select Parent Group");
    if (!level2Form.node_name.trim()) return toast.error("Please enter Group name");
    if (!level2Form.node_code.trim()) return toast.error("Please enter Group code");

    setSavingLevel2(true);
    try {
      const payload = {
        node_id: editLevel2Id ? Number(editLevel2Id) : null,
        school_id: Number(userObj.school_id),
        node_name: level2Form.node_name.trim(),
        node_level: 2,
        parent_id: Number(selectedLevel1),
        is_active: !!level2Form.is_active,
        node_code: level2Form.node_code.trim(),
        created_by: userObj.user_name,
      };

      const res = await axios.post(`${baseUrl}/coanodesave/`, payload, {
        headers: { "Content-Type": "application/json" },
      });

      toast.success(
        res?.data?.[0]?.message || (editLevel2Id ? "Group updated" : "Group created")
      );

      await fetchLevel2(selectedLevel1);
      setEditLevel2Id(null);
      setLevel2Form({ node_name: "", node_code: "", is_active: true });
    } catch (error) {
      console.error(error?.response?.data || error.message);
      toast.error(
        error?.response?.data?.message ||
          error?.response?.data?.detail ||
          error?.response?.data?.error ||
          "Error saving Group"
      );
    } finally {
      setSavingLevel2(false);
    }
  };

  // Account Head helpers
  const updateLevel3Row = (idx, key, value) => {
    setLevel3Rows((prev) =>
      prev.map((r, i) => (i === idx ? { ...r, [key]: value } : r))
    );
  };

  // ✅ allow add row even in edit mode
  const addLevel3Row = () => {
    if (!selectedLevel2) {
      toast.info("Please select Group");
      return;
    }
    setLevel3Rows((prev) => [
      ...prev,
      { node_name: "", node_code: "", is_active: true }, // CREATE row
    ]);
  };

  // ✅ allow removing only newly added rows (no node_id)
  const removeLevel3Row = (idx) => {
    setLevel3Rows((prev) => prev.filter((_, i) => i !== idx));
  };

  // ✅ Save Account Heads: UPDATE + CREATE in one click
  const handleSaveLevel3 = async () => {
    if (!selectedLevel1) return toast.error("Please select Parent Group");
    if (!selectedLevel2) return toast.error("Please select Group");

    const cleaned = level3Rows
      .map((r) => ({
        ...r,
        node_name: (r.node_name || "").trim(),
        node_code: (r.node_code || "").trim(),
      }))
      .filter((r) => r.node_name || r.node_code);

    if (cleaned.length === 0) return toast.error("Add at least one Account Head row");

    for (const r of cleaned) {
      if (!r.node_name) return toast.error("Account Head name is required");
      if (!r.node_code) return toast.error("Account Head code is required");
    }

    setSavingLevel3(true);
    try {
      let updateCount = 0;
      let createCount = 0;

      for (const row of cleaned) {
        const payload = {
          node_id: row.node_id ? Number(row.node_id) : null, // ✅ if exists => UPDATE
          school_id: Number(userObj.school_id),
          node_name: row.node_name,
          node_level: 3,
          parent_id: Number(selectedLevel2),
          is_active: row.is_active !== false,
          node_code: row.node_code,
          created_by: userObj.user_name,
        };

        await axios.post(`${baseUrl}/coanodesave/`, payload, {
          headers: { "Content-Type": "application/json" },
        });

        if (row.node_id) updateCount += 1;
        else createCount += 1;
      }

      if (updateCount && createCount) toast.success(`Updated ${updateCount} and added ${createCount} Account Head(s)`);
      else if (updateCount) toast.success(`Updated ${updateCount} Account Head(s)`);
      else toast.success(`Added ${createCount} Account Head(s)`);

      // ✅ reset after save
      setEditLevel3Id(null);
      setLevel3Rows([{ node_name: "", node_code: "", is_active: true }]);
    } catch (error) {
      console.error(error?.response?.data || error.message);
      toast.error(
        error?.response?.data?.message ||
          error?.response?.data?.detail ||
          error?.response?.data?.error ||
          "Error saving Account Heads"
      );
    } finally {
      setSavingLevel3(false);
    }
  };

  const pathText = `${selectedLevel1Obj?.node_name || "Parent Group"} → ${
    selectedLevel2Obj?.node_name || "Group"
  } → Account Head`;

  const isEditingAnything = !!editLevel2Id || !!editLevel3Id;

  const cancelEdit = () => {
    setEditLevel2Id(null);
    setEditLevel3Id(null);
    setLevel2Form({ node_name: "", node_code: "", is_active: true });
    setLevel3Rows([{ node_name: "", node_code: "", is_active: true }]);
    toast.info("Edit mode cleared");
  };

  return (
    <div className="pageBody">
      <ToastContainer />

      <Container fluid className="py-3">
        <Card className="shadow-sm border-0">
          <Card.Body className="pb-2">
            <div className="d-flex flex-wrap justify-content-between align-items-start gap-2">
              <div>
                <div className="text-muted" style={{ fontSize: 13 }}>
                  Finance / Chart of Accounts
                </div>

                <div className="d-flex align-items-center gap-2">
                  <h5 className="mb-0">COA Setup</h5>

                  <Badge bg="secondary" className="fw-normal">
                    School ID: {userObj.school_id ?? "-"}
                  </Badge>

                  {isEditingAnything ? (
                    <Badge bg="warning" text="dark" className="fw-normal">
                      Editing
                    </Badge>
                  ) : null}
                </div>

                <div className="text-muted" style={{ fontSize: 13, marginTop: 4 }}>
                  {pathText}
                </div>
              </div>

              <div className="d-flex flex-wrap gap-2 align-items-center">
                <Badge
                  bg={selectedLevel1 ? "primary" : "light"}
                  text={selectedLevel1 ? "white" : "dark"}
                  className="border"
                >
                  {selectedLevel1Obj
                    ? `Parent Group: ${selectedLevel1Obj.node_name}`
                    : "Parent Group: Not selected"}
                </Badge>

                <Badge
                  bg={selectedLevel2 ? "success" : "light"}
                  text={selectedLevel2 ? "white" : "dark"}
                  className="border"
                >
                  {selectedLevel2Obj
                    ? `Group: ${selectedLevel2Obj.node_name}`
                    : "Group: Not selected"}
                </Badge>

                {isEditingAnything ? (
                  <Button variant="outline-danger" onClick={cancelEdit}>
                    Cancel Edit
                  </Button>
                ) : null}
              </div>
            </div>
          </Card.Body>

          <div className="border-top" />

          <Card.Body>
            <Row className="g-3">
              {/* LEFT */}
              <Col xs={12} lg={4} xxl={3}>
                <Card className="border rounded-3 h-100">
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <div>
                        <div className="fw-semibold">Step 1</div>
                        <div className="text-muted" style={{ fontSize: 13 }}>
                          Choose Parent Group
                        </div>
                      </div>
                      <Badge bg="warning" text="dark">
                        Required
                      </Badge>
                    </div>

                    <Form.Group className="mb-2">
                      <Form.Label className="text-muted" style={{ fontSize: 13 }}>
                        Parent Group
                      </Form.Label>

                      <Form.Select
                        value={selectedLevel1}
                        onChange={(e) => setSelectedLevel1(e.target.value)}
                        disabled={loadingLevel1}
                      >
                        <option value="">
                          {loadingLevel1 ? "Loading..." : "Select Parent Group"}
                        </option>
                        {level1Options.map((opt) => (
                          <option key={String(opt.node_id)} value={opt.node_id}>
                            {opt.node_name}
                            {opt.node_code ? ` — ${opt.node_code}` : ""}
                          </option>
                        ))}
                      </Form.Select>

                      <div className="text-muted mt-1" style={{ fontSize: 12 }}>
                        Selecting Parent Group loads Groups.
                      </div>
                    </Form.Group>

                    <Button
                      variant="outline-secondary"
                      className="w-100"
                      onClick={fetchLevel1}
                      disabled={loadingLevel1}
                    >
                      {loadingLevel1 ? (
                        <>
                          <Spinner size="sm" className="me-2" />
                          Refreshing
                        </>
                      ) : (
                        "Refresh Parent Groups"
                      )}
                    </Button>
                  </Card.Body>
                </Card>
              </Col>

              {/* RIGHT */}
              <Col xs={12} lg={8} xxl={9}>
                <Card className="border rounded-3">
                  <Card.Body>
                    <Tabs
                      activeKey={activeTab}
                      onSelect={(k) => setActiveTab(k)}
                      className="coaTabs mb-3"
                    >
                      {/* STEP 2 */}
                      <Tab eventKey="level2" title="Step 2: Group">
                        <Row className="g-3">
                          <Col xs={12} xl={6}>
                            <Card className="border rounded-3 h-100">
                              <Card.Body>
                                <div className="fw-semibold mb-1">
                                  {editLevel2Id ? "Update Group" : "Create Group"}
                                </div>

                                <Row className="g-2">
                                  <Col xs={12}>
                                    <Form.Label className="text-muted" style={{ fontSize: 13 }}>
                                      Group Name
                                    </Form.Label>
                                    <Form.Control
                                      type="text"
                                      value={level2Form.node_name}
                                      onChange={(e) =>
                                        setLevel2Form((p) => ({
                                          ...p,
                                          node_name: e.target.value,
                                        }))
                                      }
                                      placeholder="e.g., Fixed Assets"
                                      disabled={!selectedLevel1}
                                    />
                                  </Col>

                                  <Col xs={12}>
                                    <Form.Label className="text-muted" style={{ fontSize: 13 }}>
                                      Group Code
                                    </Form.Label>
                                    <Form.Control
                                      type="text"
                                      value={level2Form.node_code}
                                      onChange={(e) =>
                                        setLevel2Form((p) => ({
                                          ...p,
                                          node_code: e.target.value,
                                        }))
                                      }
                                      placeholder="e.g., FA"
                                      disabled={!selectedLevel1}
                                    />
                                  </Col>

                                  <Col xs={12} className="d-flex gap-2 mt-2">
                                    <Button
                                      variant="success"
                                      className="flex-grow-1"
                                      onClick={handleSaveLevel2}
                                      disabled={!selectedLevel1 || savingLevel2}
                                    >
                                      {savingLevel2 ? (
                                        <>
                                          <Spinner size="sm" className="me-2" />
                                          Saving...
                                        </>
                                      ) : editLevel2Id ? (
                                        "Update Group"
                                      ) : (
                                        "Save Group"
                                      )}
                                    </Button>

                                    <Button
                                      variant="outline-secondary"
                                      onClick={() => fetchLevel2(selectedLevel1)}
                                      disabled={!selectedLevel1 || loadingLevel2}
                                    >
                                      {loadingLevel2 ? (
                                        <>
                                          <Spinner size="sm" className="me-2" />
                                          Loading...
                                        </>
                                      ) : (
                                        "Refresh"
                                      )}
                                    </Button>
                                  </Col>
                                </Row>
                              </Card.Body>
                            </Card>
                          </Col>

                          {/* Select Group */}
                          <Col xs={12} xl={6}>
                            <Card className="border rounded-3 h-100">
                              <Card.Body>
                                <div className="fw-semibold mb-1">Select Group</div>

                                <Form.Group>
                                  <Form.Label className="text-muted" style={{ fontSize: 13 }}>
                                    Group List
                                  </Form.Label>

                                  <Form.Select
                                    value={selectedLevel2}
                                    onChange={(e) => setSelectedLevel2(e.target.value)}
                                    disabled={!selectedLevel1 || loadingLevel2}
                                  >
                                    <option value="">
                                      {loadingLevel2 ? "Loading..." : "Select Group"}
                                    </option>
                                    {level2Options.map((opt) => (
                                      <option key={String(opt.node_id)} value={opt.node_id}>
                                        {opt.node_name}
                                        {opt.node_code ? ` — ${opt.node_code}` : ""}
                                      </option>
                                    ))}
                                  </Form.Select>
                                </Form.Group>
                              </Card.Body>
                            </Card>
                          </Col>
                        </Row>
                      </Tab>

                      {/* STEP 3 */}
                      <Tab eventKey="level3" title="Step 3: Account Heads (Bulk)">
                        <Row className="g-3 align-items-end mb-2">
                          <Col xs={12} md={6} xl={5}>
                            <Form.Group>
                              <Form.Label className="text-muted" style={{ fontSize: 13 }}>
                                Selected Group
                              </Form.Label>

                              <Form.Select
                                value={selectedLevel2}
                                onChange={(e) => setSelectedLevel2(e.target.value)}
                                disabled={!selectedLevel1 || loadingLevel2}
                              >
                                <option value="">
                                  {loadingLevel2 ? "Loading..." : "Select Group"}
                                </option>
                                {level2Options.map((opt) => (
                                  <option key={String(opt.node_id)} value={opt.node_id}>
                                    {opt.node_name}
                                    {opt.node_code ? ` — ${opt.node_code}` : ""}
                                  </option>
                                ))}
                              </Form.Select>
                            </Form.Group>
                          </Col>

                          <Col xs={12} md="auto" className="d-flex gap-2">
                            <Button
                              variant="outline-primary"
                              onClick={addLevel3Row}
                              disabled={!selectedLevel2}
                            >
                              + Add Row
                            </Button>

                            <Button
                              variant="success"
                              onClick={handleSaveLevel3}
                              disabled={!selectedLevel2 || savingLevel3}
                            >
                              {savingLevel3 ? (
                                <>
                                  <Spinner size="sm" className="me-2" />
                                  Saving...
                                </>
                              ) : editLevel3Id ? (
                                "Update + Add"
                              ) : (
                                "Save Account Heads"
                              )}
                            </Button>
                          </Col>
                        </Row>

                        <Card className="border rounded-3">
                          <Card.Body className="p-0">
                            <div className="p-3">
                              {level3Rows.map((row, idx) => (
                                <Row key={idx} className="g-2 align-items-center mb-2">
                                  <Col xs={12} md={6}>
                                    <Form.Control
                                      type="text"
                                      value={row.node_name}
                                      placeholder="e.g., Fee Collections"
                                      onChange={(e) =>
                                        updateLevel3Row(idx, "node_name", e.target.value)
                                      }
                                      disabled={!selectedLevel2}
                                    />
                                  </Col>

                                  <Col xs={12} md={4}>
                                    <Form.Control
                                      type="text"
                                      value={row.node_code}
                                      placeholder="e.g., RP-INC-FC"
                                      onChange={(e) =>
                                        updateLevel3Row(idx, "node_code", e.target.value)
                                      }
                                      disabled={!selectedLevel2}
                                    />
                                  </Col>

                                  <Col xs={12} md={2} className="text-md-end">
                                    <Button
                                      variant="outline-danger"
                                      size="sm"
                                      onClick={() => removeLevel3Row(idx)}
                                      disabled={
                                        level3Rows.length === 1 ||
                                        (editLevel3Id && !!row.node_id) // ✅ prevent removing edited row
                                      }
                                      title="Remove row"
                                    >
                                      ✕
                                    </Button>
                                  </Col>
                                </Row>
                              ))}
                            </div>
                          </Card.Body>
                        </Card>
                      </Tab>
                    </Tabs>

                    <div className="d-flex justify-content-end">
                      <Button variant="outline-secondary" onClick={() => navigate(-1)}>
                        Back
                      </Button>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      </Container>

      <style>{`
        .coaTabs .nav-link { font-size: 14px; padding: 10px 14px; }
        .coaTabs .nav-link.active { font-weight: 600; }
      `}</style>
    </div>
  );
};

export default AddChartOfAccounts;